"use client";

import { useEffect, useState, useRef } from "react";
import Link from "next/link";
import "./styles.css";

export default function LivestreamComingSoon() {
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  // This page always opens in a new tab (target="_blank" rel="noopener noreferrer"),
  // which strips document.referrer and clears history. The only reliable signals are:
  //   1. ?from=slug — added by withInvitationBackHint when the link is the coming-soon URL
  //   2. window.close() — closes the new tab and returns focus to the invitation tab
  const goBack = () => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);
    const from = params.get("from");
    const invitationUrl = from && /^[a-z0-9-]+$/i.test(from)
      ? `/invitation/${encodeURIComponent(from)}`
      : null;

    // Primary: close this tab — returns the user to the invitation tab they came from.
    // If window.close() is blocked by the browser, the setTimeout fallback navigates instead.
    window.close();

    setTimeout(() => {
      if (invitationUrl) {
        window.location.href = invitationUrl;
      } else if (document.referrer) {
        try {
          const ref = new URL(document.referrer);
          if (ref.origin === window.location.origin && !ref.pathname.startsWith("/livestream")) {
            window.location.href = document.referrer;
            return;
          }
        } catch {
          // ignore malformed referrer
        }
        window.location.href = "/";
      } else {
        window.location.href = "/";
      }
    }, 300);
  };

  return (
    <div className="ls-coming-soon" ref={containerRef}>
      {/* Animated background elements */}
      <div className="ls-bg-elements">
        <div className="ls-bg-circle ls-bg-circle--1" />
        <div className="ls-bg-circle ls-bg-circle--2" />
        <div className="ls-bg-circle ls-bg-circle--3" />
        <div className="ls-bg-glow" />
      </div>

      {/* Decorative mandala pattern */}
      <div className="ls-mandala ls-mandala--top" />
      <div className="ls-mandala ls-mandala--bottom" />

      {/* Twinkling stars scattered across the page */}
      <div className="ls-particles">
        {mounted && Array.from({ length: 25 }).map((_, i) => (
          <div
            key={i}
            className="ls-particle"
            style={{
              left: `${5 + Math.random() * 90}%`,
              top: `${5 + Math.random() * 90}%`,
              animationDelay: `${Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      <main className="ls-content">
        {/* Decorative top flourish */}
        <div className="ls-flourish">
          <svg viewBox="0 0 200 40" className="ls-flourish__svg">
            <path
              d="M0 20 Q 50 0, 100 20 Q 150 40, 200 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
            />
            <circle cx="100" cy="20" r="4" fill="currentColor" />
            <circle cx="70" cy="15" r="2" fill="currentColor" />
            <circle cx="130" cy="15" r="2" fill="currentColor" />
          </svg>
        </div>

        {/* Main icon - video camera with play button */}
        <div className="ls-icon">
          <div className="ls-icon__ring ls-icon__ring--outer" />
          <div className="ls-icon__ring ls-icon__ring--inner" />
          <div className="ls-icon__center">
            <svg viewBox="0 0 64 64" className="ls-icon__svg">
              <rect x="8" y="18" width="36" height="28" rx="4" fill="none" stroke="currentColor" strokeWidth="2.5" />
              <path d="M44 28 L56 20 L56 44 L44 36 Z" fill="currentColor" opacity="0.8" />
              <polygon points="24,26 24,38 34,32" fill="currentColor" />
            </svg>
          </div>
          <div className="ls-icon__pulse" />
        </div>

        <h1 className="ls-title">
          <span className="ls-title__line">Live Stream</span>
          <span className="ls-title__accent">Coming Soon</span>
        </h1>

        <div className="ls-divider">
          <span className="ls-divider__line" />
          <span className="ls-divider__diamond" />
          <span className="ls-divider__line" />
        </div>

        <p className="ls-message">
          The live wedding ceremony broadcast will begin
          <strong> approximately 1 hour before</strong> the wedding starts.
        </p>

        <p className="ls-submessage">
          Please check the invitation for the exact ceremony timing
          and return here closer to the event.
        </p>

        {/* Info card */}
        <div className="ls-info-card">
          <div className="ls-info-card__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 6v6l4 2" />
            </svg>
          </div>
          <div className="ls-info-card__text">
            <span className="ls-info-card__label">When to return</span>
            <span className="ls-info-card__value">1 hour before the ceremony begins</span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="ls-actions">
          <button onClick={goBack} className="ls-btn ls-btn--primary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ls-btn__icon">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Invitation
          </button>
        </div>

        {/* Promo section */}
        <div className="ls-promo">
          <div className="ls-promo__divider" />
          <p className="ls-promo__text">
            Love this invitation? Create your own stunning digital wedding invitation
          </p>
          <Link href="https://myshaadhilink.in" className="ls-btn ls-btn--secondary">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="ls-btn__icon">
              <path d="M12 5v14M5 12h14" />
            </svg>
            Create Your Invitation
          </Link>
        </div>

        {/* Branding footer */}
        <footer className="ls-footer">
          <Link href="https://myshaadhilink.in" className="ls-footer__brand">
            <span className="ls-footer__brand-text">Powered by</span>
            <span className="ls-footer__brand-name">MyShaadhiLink</span>
          </Link>
          <p className="ls-footer__tagline">Premium Digital Wedding Invitations</p>
        </footer>
      </main>
    </div>
  );
}
